import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useMemo, useCallback } from 'react';
import cns from '../../../../node_modules/classnames/index.js';
import useComponentId from '../../Hooks/useComponentId.js';
import { NOOP } from '../../../../utils/const.js';
import FormFieldFrame from '../FormFieldFrame/FormFieldFrame.js';
import { isUndefined } from '../../../../utils/js.js';
import useRestrictions from './useRestrictions.js';
import { getInputFormat, InputFormats } from '../shared.js';
import useForwardRefOrRef from '../../../Hooks/useForwardRefOrRef.js';
import { getFormFieldClassName } from '../classUtilities.js';
import { InputStates } from '../common.js';
import Restriction from '../../../Restriction/Restriction.js';
import EllipsisText from '../../../EllipsisText/EllipsisText.js';
import { Align } from '../../../Utils/numeriHelper.js';
import useFieldFlow from './useFieldFlow.js';
import useRefWithSelectionRange from '../../Hooks/useRefWithSelectionRange.js';
import { EmphasisLM } from '../../../types.js';

function getInputClassName({ state, disabled, readOnly, className, }) {
    return cns('v2-text-field', getFormFieldClassName(state, disabled, readOnly), className);
}
function getBaseInputClassName({ format, align }) {
    const inputFormat = getInputFormat(format);
    return cns('v2-text-field__input', `v2-text-field__input--align-${align}`, inputFormat.mapTextTransorm ? `v2-text-field__input--text-format-${inputFormat.mapTextTransorm()}` : undefined);
}
const TextFieldComp = React__default.forwardRef(({ action, align, className, disabled = false, inputProps, onActionClick, onChange = NOOP, onKeyDown, placeholder, precision, readOnly = false, restrictions = [], state, stateMessage, title, helperText, componentHeight, titleIcon, value, icon, format, emphasis = EmphasisLM.MEDIUM, tooltipProps, flowMode, }, ref) => {
    const id = useComponentId();
    const [inputRef, myRef] = useForwardRefOrRef(ref);
    const handleRestictionKeyDown = useRestrictions(restrictions, format, precision, onKeyDown);
    const handleKeyDown = useFieldFlow(flowMode, handleRestictionKeyDown);
    const inputNormalizeText = useMemo(() => {
        const { normalizeText } = getInputFormat(format);
        return normalizeText;
    }, [format]);
    const handleChange = useCallback((evt) => {
        let newValue = evt.target.value;
        if (inputNormalizeText) {
            newValue = inputNormalizeText(newValue, precision);
        }
        onChange(newValue, evt);
    }, [inputNormalizeText, onChange, precision]);
    const stringValue = isUndefined(value) ? '' : value;
    useRefWithSelectionRange(myRef, stringValue, !!inputNormalizeText);
    return (jsxs(FormFieldFrame, { className: getInputClassName({
            state, disabled, readOnly, className,
        }), onClick: () => {
            if (!readOnly && myRef.current) {
                myRef.current.focus();
            }
        }, title: title, titleIcon: titleIcon, state: state, stateMessage: stateMessage, componentHeight: componentHeight, helperText: helperText, icon: icon, readOnly: readOnly, disabled: disabled, action: action, onActionClick: onActionClick, emphasis: emphasis, tooltipProps: tooltipProps, children: [readOnly && jsx(EllipsisText, { top: false, children: stringValue === '' ? '-' : stringValue }), !readOnly && typeof title === 'string' && jsx("label", { htmlFor: id, children: title }), !readOnly && (jsx("input", Object.assign({ ref: inputRef }, inputProps, { className: getBaseInputClassName({ format, align }), id: id, value: stringValue, disabled: disabled || readOnly, placeholder: readOnly ? undefined : placeholder, onChange: handleChange, onKeyDown: handleKeyDown })))] }));
});
TextFieldComp.defaultProps = {
    action: undefined,
    className: undefined,
    componentHeight: undefined,
    disabled: false,
    onActionClick: NOOP,
    onChange: NOOP,
    onKeyDown: undefined,
    placeholder: undefined,
    readOnly: false,
    helperText: undefined,
    state: undefined,
    stateMessage: undefined,
    title: undefined,
    titleIcon: undefined,
    icon: undefined,
    inputProps: undefined,
    format: undefined,
    value: undefined,
    restrictions: undefined,
    precision: undefined,
    align: Align.left,
    emphasis: EmphasisLM.MEDIUM,
    tooltipProps: undefined,
    flowMode: undefined,
};
/**
Componente per l'inserimento non assistito di un dato.

__Il componente è la versione controlled di `Input`. Se possibile utilizzare questo componente__

Il componente è "controlled" questo significa che il valore dell'input è gestito esternamente dal componente mediante
la proprietà value e onChange.
Lo stesso vale per la quasi totalità di proprietà, non ci sono automatismi particolari.

Ref:
- input reference. ATTENZIONE: la input ref è definita solo nel caso NON readOnly.

Constants:

- `TextField.Formats`: formati ammessi per la proprietà format;
- `TextField.States`: stati ammessi per la proprietà state;
- `TextField.Restrictions`: restrizioni ammesse, vedi Restrictions;

_Differenze con componente V1:_

- Non ha gestione un controlled/form. Utilizzare componente V2/Input
- nameCase sostituito da format=TextField.Formats.capitalize
- actions non gestito, vedere se è possibile coprire la funzionalità con action/icon
- titleLines non gestito, i titolo vanno messi su una sola riga
- zeroValid non gestito. Utilizzare V2/Input con format=Input.Formats.number
- required non gestito. Per evidenziare questo stato il programmatore deve gestire autonomamente
il titolo in base alle indicazioni UX (tipicamente mettendo "opzionale")
*/
const TextField = TextFieldComp;
TextField.States = InputStates;
TextField.Formats = InputFormats;
TextField.Restrictions = Restriction.DefaultValues;

export { TextField as default };
